/** Presentation derived from a GameSpec — never hand-written per game.
 * Render kinds = archetypes; each kind's animation states = the union of the
 * states its modules (and tag-granted globals) declare they can set. The art
 * plan is therefore a pure function of the spec: one sprite per kind, one
 * strip per kind × state, one background. */
import type { AnimStateName } from '../animStates.js';
import type { AnyComposeModule, EventPresentationV1, HudFieldDeclV1 } from './module.js';
import { compileGameSpec, type CompiledGameV1 } from './compile.js';
import { seatRelation, type ArchetypeRenderV1, type ComposedCameraV1, type GameSpecV1, type SeatRelationV1 } from './spec.js';

export interface ComposedPresentationEntityV1 {
  role: string;
  tags: string[];
  size: { w: number; h: number };
  render: ArchetypeRenderV1;
  /** Animation states this kind can show (art strips), in vocabulary order. */
  states: AnimStateName[];
}

export interface ComposedPresentationV1 {
  title: string;
  summary: string;
  projection: GameSpecV1['presentation']['projection'];
  backgroundRole: string;
  /** The simulated world (a follow camera's level). */
  world: { w: number; h: number };
  /** How the screen frames the world — the spec's camera, `fixed` when it
   * declares none. */
  camera: ComposedCameraV1;
  orientation: GameSpecV1['world']['orientation'];
  palette: string[];
  backgroundPaletteIndex: number;
  entities: Record<string, ComposedPresentationEntityV1>;
  hud: HudFieldDeclV1[];
  input: GameSpecV1['input'];
  /** Every presentation event kind the runtime may emit (cue/vfx tables). */
  events: string[];
  /** Event kind → its declaring module's default cue/vfx. */
  effects: Record<string, EventPresentationV1>;
  /** Player seat kinds (index == slot). */
  players: string[];
  /** How the seats relate (`solo` for one seat): a versus HUD shows every
   * seat's per-seat values, a solo or co-op one the viewer's. */
  relation: 'solo' | SeatRelationV1;
  /** True when one of the game's modules makes a body stand on, walk along,
   * climb or land on a surface the game draws itself (the module's
   * `surfaceContact`): its background must paint none. */
  surfaceContact: boolean;
}

export function presentationOf(compiled: CompiledGameV1): ComposedPresentationV1 {
  const { spec } = compiled;
  return structuredClone({
    title: spec.title, summary: spec.summary, projection: spec.presentation.projection,
    backgroundRole: spec.presentation.backgroundRole, world: { w: spec.world.w, h: spec.world.h },
    camera: spec.camera ?? { mode: 'fixed' as const },
    orientation: spec.world.orientation, palette: spec.presentation.palette,
    backgroundPaletteIndex: spec.presentation.backgroundPaletteIndex,
    entities: Object.fromEntries(Object.entries(spec.archetypes).map(([kind, a]) => [kind, {
      role: a.role, tags: a.tags, size: a.size, render: a.render, states: compiled.animStates[kind]!,
    }])),
    hud: compiled.hud.map(h => h.decl), input: spec.input, events: compiled.events, players: spec.players.archetypes,
    relation: seatRelation(spec),
    surfaceContact: compiled.instances.some(i => i.module.surfaceContact === true),
    effects: Object.fromEntries(compiled.events.map(kind => [kind,
      compiled.instances.find(i => i.module.events.includes(kind))!.module.presentation[kind]!])),
  });
}

/** Validate + compile, then derive (throws GameSpecError on an invalid spec). */
export function deriveGameSpecPresentation(spec: unknown, registry: ReadonlyMap<string, AnyComposeModule>): ComposedPresentationV1 {
  return presentationOf(compileGameSpec(spec, registry));
}
